import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subscription } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { EstoqueLoteSerieInlineComponent } from '@shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { LoteSaidaEstoqueModalItem } from '@shared/components/lote-saida-estoque-modal/lote-saida-estoque-modal.component';
import { SerieSaidaEstoqueModalItem } from '@shared/components/serie-saida-estoque-modal/serie-saida-estoque-modal.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { LookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { CriarReservaManualDiretaRequest } from '../../../../../shared/services/requests/produto/criar-reserva-manual-direta.request';
import { MaskService } from '../../../../../shared/services/mask.service';

export interface NovaReservaProdutoDialogData {
  idProduto: number;
  descricaoProduto: string;
  skuProduto?: string;
  unidade?: string;
  isControlaLotes: boolean;
  isControlaSeries: boolean;
  isFracionada: boolean;
  /** Local que abre selecionado: o filtrado na aba ou, com filtro em "todos", o padrão do sistema. */
  idLocalizacaoEstoque: number | null;
}

@Component({
  selector: 'app-nova-reserva-produto-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    NgxCurrencyDirective,
    EstoqueLoteSerieInlineComponent,
    AlertCardComponent,
    AbntPipe,
  ],
  templateUrl: './nova-reserva-produto-dialog.component.html',
  styleUrl: './nova-reserva-produto-dialog.component.css',
})
export class NovaReservaProdutoDialogComponent implements OnInit {
  public isSalvando = false;
  public localizacoes: any[] = [];
  public idLocalizacaoEstoque: number | null = null;
  public dataValidadeReserva: Date | null = null;
  public descricao = '';
  public quantidade = 1;

  public lotes: LoteSaidaEstoqueModalItem[] = [];
  public series: SerieSaidaEstoqueModalItem[] = [];

  /** Saldo disponível do produto no local escolhido (null enquanto não carregou ou sem local). */
  public disponivelLocal: number | null = null;
  private disponivelSubscription?: Subscription;

  // Unidade não fracionada só aceita quantidade inteira — mesma máscara da Nova Movimentação.
  get quantidadeMask() {
    return this.data.isFracionada ? this.maskService.getDynamicDecimalMask() : this.maskService.getIntegerMask();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NovaReservaProdutoDialogData,
    private dialogRef: MatDialogRef<NovaReservaProdutoDialogComponent>,
    private alertService: AlertService,
    private lookupService: LookupService,
    private controleEstoqueService: ControleEstoqueService,
    private maskService: MaskService,
  ) {}

  public ngOnInit(): void {
    this.idLocalizacaoEstoque = this.data.idLocalizacaoEstoque;
    this.carregarDisponivelLocal();

    this.lookupService.getLocalizacaoEstoqueSelect().subscribe({
      next: response => {
        if (!response?.isError) {
          this.localizacoes = response.list ?? [];
        } else {
          this.alertService.show('Aviso', response.errorDescription || 'Não foi possível carregar os locais de estoque.', AlertType.warning);
        }
      },
      error: () => {
        this.alertService.show('Erro', 'Não foi possível carregar os locais de estoque.', AlertType.error);
      },
    });
  }

  public get possuiRastreabilidade(): boolean {
    return this.data.isControlaLotes || this.data.isControlaSeries;
  }

  public get excedeDisponivel(): boolean {
    return this.disponivelLocal !== null && this.quantidade > this.disponivelLocal;
  }

  public get podeConfirmar(): boolean {
    return !this.isSalvando && !!this.idLocalizacaoEstoque && this.quantidade > 0;
  }

  public onLocalChange(): void {
    this.carregarDisponivelLocal();
  }

  // Mesmo saldo do rodapé da tela (Total disponível), filtrado pelo local escolhido.
  private carregarDisponivelLocal(): void {
    this.disponivelSubscription?.unsubscribe();
    this.disponivelLocal = null;
    if (!this.idLocalizacaoEstoque) return;

    this.disponivelSubscription = this.controleEstoqueService
      .getCabecalho(this.data.idProduto, this.idLocalizacaoEstoque)
      .subscribe({
        next: response => {
          if (!response?.isError) this.disponivelLocal = response.cabecalho?.totalDisponivel ?? null;
        },
      });
  }

  public get quantidadeRastreada(): number {
    if (this.data.isControlaLotes) {
      return this.lotes.reduce((total, lote) => total + (lote.quantidadeSaida || 0), 0);
    }
    if (this.data.isControlaSeries) {
      return this.series.length;
    }
    return 0;
  }

  /** Lotes ou séries ativos para o painel inline, conforme o controle do produto. */
  public get itensRastreabilidade(): (LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[] {
    if (this.data.isControlaLotes) return this.lotes;
    if (this.data.isControlaSeries) return this.series;
    return [];
  }

  public onItensRastreabilidadeChange(itens: unknown[]): void {
    if (this.data.isControlaLotes) this.lotes = itens as LoteSaidaEstoqueModalItem[];
    else if (this.data.isControlaSeries) this.series = itens as SerieSaidaEstoqueModalItem[];
  }

  public cancelar(): void {
    if (!this.isSalvando) this.dialogRef.close({ confirmed: false });
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (teclasPermitidas.includes(event.key) || isAtalho) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  public onDataValidadeReservaBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    this.dataValidadeReserva = this.lerDataInput(input);
  }

  private lerDataInput(input: HTMLInputElement): Date | null {
    const valor = (input?.value || '').trim();
    if (!valor) return null;
    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return null;
    }
    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;
    const data = this.parseDataBr(formatado);
    if (!data) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
    }
    return data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;
    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);
    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);
    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
    return data;
  }

  public salvar(): void {
    if (!this.validar()) return;

    this.isSalvando = true;
    const request = this.montarRequest();
    this.controleEstoqueService.criarReservaManual(request).subscribe({
      next: response => {
        this.isSalvando = false;
        if (response?.isError) {
          this.alertService.show('Aviso', response?.errorDescription || 'Não foi possível salvar a reserva.', AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Reserva realizada com sucesso!', AlertType.success);
        this.dialogRef.close({ confirmed: true });
      },
      error: () => {
        this.isSalvando = false;
        this.alertService.show('Erro', 'Não foi possível salvar a reserva.', AlertType.error);
      },
    });
  }

  private montarRequest(): CriarReservaManualDiretaRequest {
    return {
      idProduto: this.data.idProduto,
      idLocalizacaoEstoque: this.idLocalizacaoEstoque!,
      quantidade: this.quantidade,
      dataValidadeReserva: this.dataValidadeReserva ? this.formatarData(this.dataValidadeReserva) : undefined,
      descricao: this.descricao.trim() || undefined,
      lotes: this.lotes.map(lote => ({
        idLote: lote.idLote,
        codigoLote: lote.codigoLote,
        quantidade: lote.quantidadeSaida,
      })),
      series: this.series.map(serie => ({ idSerie: serie.idSerie, codigoSerie: serie.codigoSerie })),
    };
  }

  private validar(): boolean {
    if (!this.idLocalizacaoEstoque) {
      this.alertService.show('Atenção', 'Selecione o local de estoque.', AlertType.warning);
      return false;
    }

    if (!(this.quantidade > 0)) {
      this.alertService.show('Atenção', 'Informe uma quantidade maior que zero.', AlertType.warning);
      return false;
    }

    // A API também valida; a tela antecipa para não ir ao servidor.
    if (this.excedeDisponivel) {
      this.alertService.show('Atenção', 'Quantidade a reservar excede o saldo disponível.', AlertType.warning);
      return false;
    }

    if (!this.isRastreabilidadeValida()) {
      const tipo = this.data.isControlaLotes ? 'lotes' : 'séries';
      this.alertService.show('Atenção', `Informe os ${tipo} correspondentes à quantidade da reserva.`, AlertType.warning);
      return false;
    }

    return true;
  }

  private isRastreabilidadeValida(): boolean {
    if (this.data.isControlaLotes) {
      return this.lotes.length > 0 && Math.abs(this.quantidadeRastreada - this.quantidade) < 0.0001;
    }
    if (this.data.isControlaSeries) {
      return this.series.length > 0 && this.series.length === this.quantidade;
    }
    return true;
  }

  private formatarData(data: Date): string {
    return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
  }
}
